/**
 * Companion profiles — pets, chosen family, and other archive members.
 */

export const PROFILE_TYPE = {
  PERSON: 'person',
  PET: 'pet',
  COMPANION: 'companion',
};

export const COMPANION_TYPE = {
  FAMILY_PET: 'family_pet',
  CHOSEN_FAMILY: 'chosen_family',
  FRIEND: 'friend',
  CARER: 'carer',
  OTHER: 'other',
};

export const PET_SPECIES = [
  { value: 'dog', label: 'Dog' },
  { value: 'cat', label: 'Cat' },
  { value: 'horse', label: 'Horse' },
  { value: 'bird', label: 'Bird' },
  { value: 'other', label: 'Other' },
];

export const COMPANION_TYPE_OPTIONS = [
  { value: COMPANION_TYPE.CHOSEN_FAMILY, label: 'Chosen family' },
  { value: COMPANION_TYPE.FRIEND, label: 'Close friend' },
  { value: COMPANION_TYPE.CARER, label: 'Carer' },
  { value: COMPANION_TYPE.OTHER, label: 'Other important connection' },
];

export function getProfileType(person) {
  return person?.profile_type || PROFILE_TYPE.PERSON;
}

export function isGenealogicalPerson(person) {
  return getProfileType(person) === PROFILE_TYPE.PERSON;
}

export function isCompanionProfile(person) {
  const t = getProfileType(person);
  return t === PROFILE_TYPE.PET || t === PROFILE_TYPE.COMPANION;
}

export function getDisplayName(person) {
  if (!person) return '';
  const gn = (person.given_names || '').trim();
  const sn = (person.surname || '').trim();
  if (getProfileType(person) === PROFILE_TYPE.PET) {
    return gn;
  }
  return [gn, sn].filter(Boolean).join(' ');
}

export function getProfileBadgeLabel(person) {
  const type = getProfileType(person);
  if (type === PROFILE_TYPE.PET) return 'Family pet';
  if (type === PROFILE_TYPE.COMPANION) {
    switch (person?.companion_type) {
      case COMPANION_TYPE.CHOSEN_FAMILY:
        return 'Chosen family';
      case COMPANION_TYPE.FRIEND:
        return 'Close friend';
      case COMPANION_TYPE.CARER:
        return 'Carer';
      default:
        return 'Chosen family';
    }
  }
  return null;
}

export function getBirthDateLabel(person) {
  const type = getProfileType(person);
  if (type === PROFILE_TYPE.PET) return 'Joined family (optional)';
  if (type === PROFILE_TYPE.COMPANION) return 'Known from (optional)';
  return 'Born';
}

export function getDeathDateLabel(person) {
  const type = getProfileType(person);
  if (type === PROFILE_TYPE.PET) return 'Remembered until (optional)';
  if (type === PROFILE_TYPE.COMPANION) return 'Remembered until (optional)';
  return 'Died';
}

export function getBiographicalNotesLabel(person) {
  const type = getProfileType(person);
  if (type === PROFILE_TYPE.PET) return 'Known for / remembered for';
  if (type === PROFILE_TYPE.COMPANION) return 'Remembered for';
  return 'Biographical notes';
}

export function filterPeopleByGroup(people, group) {
  const list = people || [];
  switch (group) {
    case 'people':
      return list.filter(isGenealogicalPerson);
    case 'pets':
      return list.filter((p) => getProfileType(p) === PROFILE_TYPE.PET);
    case 'companions':
      return list.filter((p) => getProfileType(p) === PROFILE_TYPE.COMPANION);
    default:
      return list;
  }
}

export function countArchiveMembers(people) {
  const list = people || [];
  const genealogical = list.filter(isGenealogicalPerson).length;
  const pets = list.filter((p) => getProfileType(p) === PROFILE_TYPE.PET).length;
  const companions = list.filter((p) => getProfileType(p) === PROFILE_TYPE.COMPANION).length;
  return { total: list.length, genealogical, pets, companions, archiveMembers: pets + companions };
}
